import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import { Header } from '@/@vxapp/@Enums/Header'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { AuthError } from '@/@vxapp/@Enums/PHP/core/Enums/Errors/AuthError'

export type SessionEndReason = 'expired' | 'invalid'

export interface SessionEndOwner {
  apiGUID: string
  userGUID: string
}

export interface SessionEndedContext {
  /** The signed-in tenant and user captured before the auth state was reset. */
  owner: SessionEndOwner | null
}

/* eslint-disable no-unused-vars -- Function-type parameter names are not runtime bindings. */
export type SessionEndedHandler = (
  reason: SessionEndReason,
  context: SessionEndedContext
) => void | Promise<void>
/* eslint-enable no-unused-vars */

interface SessionEndError {
  responseCode?: number
  error?: string
  data?: { error?: unknown } | null
}

interface SessionEndRequest {
  url?: string
  method?: string
  headers?: unknown
}

// api-vxapp#534 adds SESSION_EXPIRED; replace this local constant with the generated
// AuthError.SESSION_EXPIRED once the Stores enums are regenerated.
const SESSION_EXPIRED = 'SESSION_EXPIRED'
const CURRENT_SESSION_LOGOUT = { url: '/Kernal/Auth/Logout', method: 'post' }

export const SESSION_END_NOTICE_MESSAGES: Readonly<Record<SessionEndReason, string>> = {
  expired:
    'Your session expired after a period of inactivity. Sign in to continue where you left off.',
  invalid: 'Your session has ended. Sign in to continue where you left off.',
}

export const isSessionEndReason = (value: unknown): value is SessionEndReason =>
  value === 'expired' || value === 'invalid'

/** Maps an auth error code to the session-ended reason it represents, if any. */
export function sessionEndReasonForCode(code: unknown): SessionEndReason | null {
  if (code === SESSION_EXPIRED) return 'expired'
  if (code === AuthError.NO_SESSION_FOUND_INVALID_TOKEN) return 'invalid'

  return null
}

const isRequest = (
  request: SessionEndRequest | undefined,
  endpoint: { url: string; method?: string }
): boolean =>
  String(request?.url ?? '').split('?')[0] === endpoint.url &&
  String(request?.method ?? 'get').toLowerCase() === (endpoint.method ?? 'get')

const sentAuthorization = (headers: unknown): boolean => {
  if (!headers || typeof headers !== 'object') return false
  const getter = (headers as { get?: unknown }).get
  if (typeof getter === 'function') return Boolean(getter.call(headers, Header.Auth))

  return Boolean((headers as Record<string, unknown>)[Header.Auth])
}

/**
 * Classifies an API error as the end of the signed-in session.
 *
 * A string is treated as an auth error code from session renewal. An HTTP error qualifies
 * as `401 SESSION_EXPIRED` or `403 NO_SESSION_FOUND_INVALID_TOKEN` on an authenticated
 * request other than sign-in, guest authentication or current-session logout. Any other 403
 * is a page-level permission denial and does not end the session.
 */
export function classifySessionEnd(
  error: SessionEndError | string | null | undefined,
  request?: SessionEndRequest
): SessionEndReason | null {
  if (typeof error === 'string') return sessionEndReasonForCode(error)
  if (!error) return null

  const code = error.error ?? error.data?.error
  const reason = sessionEndReasonForCode(code)
  if (!reason) return null
  if (reason === 'expired' && error.responseCode !== ResponseCode.UNAUTHORIZED) return null
  if (reason === 'invalid' && error.responseCode !== ResponseCode.FORBIDDEN) return null
  if (!request) return reason
  if (
    isRequest(request, Endpoint.Auth.login) ||
    isRequest(request, Endpoint.Auth.guests) ||
    isRequest(request, CURRENT_SESSION_LOGOUT) ||
    !sentAuthorization(request.headers)
  )
    return null

  return reason
}

let sessionEndedHandler: SessionEndedHandler | null = null

/** Registers the single application handler that opens sign-in after a session ends. */
export function registerSessionEndedHandler(handler: SessionEndedHandler | null): void {
  sessionEndedHandler = handler
}

export async function notifySessionEnded(
  reason: SessionEndReason,
  context: SessionEndedContext = { owner: null }
): Promise<void> {
  await sessionEndedHandler?.(reason, context)
}
